/**
 * FileTreeItem Component
 *
 * Individual tree node with status icons:
 * - Draft (yellow)
 * - Live (green)
 * - Locked (padlock)
 * - Has changes (dot)
 */

import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FileJson,
  FileCode,
  Palette,
  Route,
  ListChecks,
  BarChart3,
  Lock,
  CircleDot,
} from "lucide-react";
import type { ReactElement } from "react";
import { useCallback } from "react";

import type { FileTreeNode, ConfigType } from "../../types";
import { CONFIG_STATUS_INFO } from "../../types";

const CONFIG_TYPE_ICONS: Readonly<Record<ConfigType, React.ComponentType<{ className?: string }>>> =
  {
    schema: FileCode,
    eligibility: ListChecks,
    ranking: BarChart3,
    journey: Route,
    branding: Palette,
  } as const;

interface FileTreeItemProps {
  readonly node: FileTreeNode;
  readonly depth: number;
  readonly isSelected: boolean;
  readonly isExpanded: boolean;
  readonly onSelect: (path: string) => void;
  readonly onToggle: (path: string) => void;
}

export function FileTreeItem({
  node,
  depth,
  isSelected,
  isExpanded,
  onSelect,
  onToggle,
}: FileTreeItemProps): ReactElement {
  const isFolder = node.type === "folder";
  const hasChildren = node.children && node.children.length > 0;

  const handleClick = useCallback(() => {
    if (isFolder && hasChildren) {
      onToggle(node.path);
    } else if (!isFolder) {
      onSelect(node.path);
    }
  }, [isFolder, hasChildren, node.path, onSelect, onToggle]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
      if (event.key === "ArrowRight" && isFolder && !isExpanded) {
        event.preventDefault();
        onToggle(node.path);
      }
      if (event.key === "ArrowLeft" && isFolder && isExpanded) {
        event.preventDefault();
        onToggle(node.path);
      }
    },
    [handleClick, isFolder, isExpanded, node.path, onToggle]
  );

  const paddingLeft = `${depth * 12 + 8}px`;

  return (
    <div role="treeitem" aria-selected={isSelected} aria-expanded={isExpanded}>
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center gap-1.5 rounded py-1 text-left text-sm transition-colors ${
          isSelected ? "bg-primary/15 text-primary" : "text-foreground hover:bg-background"
        }`}
        style={{ paddingLeft }}
      >
        {/* Expand/Collapse Icon */}
        <span className="flex h-4 w-4 items-center justify-center">
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-foreground-muted" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-foreground-muted" />
            )
          ) : (
            <span className="w-3.5" />
          )}
        </span>

        {/* Icon */}
        <NodeIcon type={node.type} configType={node.configType} isExpanded={isExpanded} />

        {/* Name */}
        <span className="flex-1 truncate">{node.name}</span>

        {/* Status Indicators */}
        {!isFolder && <StatusIndicators node={node} />}
      </button>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div role="group">
          {node.children?.map((child) => (
            <FileTreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              isSelected={isSelected && child.path === node.path}
              isExpanded={isExpanded}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface NodeIconProps {
  readonly type: "folder" | "file";
  readonly configType: ConfigType | undefined;
  readonly isExpanded: boolean;
}

function NodeIcon({ type, configType, isExpanded }: NodeIconProps): ReactElement {
  if (type === "folder") {
    const Icon = isExpanded ? FolderOpen : Folder;
    return <Icon className="h-4 w-4 text-warning" />;
  }

  if (configType && configType in CONFIG_TYPE_ICONS) {
    const Icon = CONFIG_TYPE_ICONS[configType];
    return <Icon className="h-4 w-4 text-primary" />;
  }

  return <FileJson className="h-4 w-4 text-foreground-muted" />;
}

interface StatusIndicatorsProps {
  readonly node: FileTreeNode;
}

function StatusIndicators({ node }: StatusIndicatorsProps): ReactElement {
  const { status, isLocked, hasChanges } = node;

  return (
    <span className="flex items-center gap-1">
      {/* Has unsaved changes */}
      {hasChanges && (
        <span title="Has unsaved changes">
          <CircleDot className="h-3 w-3 text-warning" />
        </span>
      )}

      {/* Lock indicator */}
      {isLocked && (
        <span title={`Locked${node.lockedBy ? ` by ${node.lockedBy}` : ""}`}>
          <Lock className="h-3 w-3 text-foreground-muted" />
        </span>
      )}

      {/* Status badge */}
      {status && (
        <span
          className={`h-2 w-2 rounded-full ${
            status === "live"
              ? "bg-success"
              : status === "draft"
                ? "bg-warning"
                : "bg-foreground-muted"
          }`}
          title={CONFIG_STATUS_INFO[status].label}
        />
      )}
    </span>
  );
}
